import {
  ArgumentError,
  AuthTokenNotSetError,
  UnknownApiError,
} from './errors';

export default function (api) {
  /**
   * @memberof API
   * @instance
   */
  const myList = {
    /**
     * This method gets ID:s that have been added to the 'favourites list' of
     * the logged in user. The asset ID:s are returned as an array of strings.
     *
     * Note: This method has limited use in practice as it doesn't return the
     * actual assets. The idea is to use the 'my-list' api to add and remove
     * assets that will later show up when getting the root category (using
     * 'browse').
     *
     * Auth token is required.
     *
     * @method
     * @this API
     *
     * @return {Promise} promise
     * @fulfill {Array.<String>}
     * @reject {AuthTokenNotSetError}
     * @reject {UnknownApiError} Server returned with unexpected status code
     * */
    getFavourites: function getFavourites() {
      if (!this.authData) {
        return Promise.reject(new AuthTokenNotSetError());
      }

      return this.request('get', '/my-list/v1/favourites')
        .then((res) => {
          switch (res.status) {
            case 200: {
              return res.body;
            }
            default: {
              throw new UnknownApiError('server returned with unhandled status code', res);
            }
          }
        });
    }.bind(api),

    /**
     * This method adds an asset to the list of favourite assets of the logged in user.
     * There is no validation of the asset ID so any string could be stored as a
     * favourite.
     *
     * Auth token is required.
     *
     * @method
     * @this API
     *
     * @param {Object} payload
     * @param {string=} payload.assetId
     *
     * @return {Promise} promise
     * @fulfill {Object}
     * @reject {AuthTokenNotSetError}
     * @reject {ArgumentError} Asset ID is required
     * @reject {UnknownApiError} Server returned with unexpected status code
     * */
    addFavourite: function addFavourite(payload = {}) {
      if (!this.authData) {
        return Promise.reject(new AuthTokenNotSetError());
      }

      if (typeof payload.assetId !== 'string') {
        return Promise.reject(new ArgumentError('payload.assetId is required and must be a string'));
      }

      const {
        assetId,
      } = payload;

      return this.request('put', `/my-list/v1/favourites/${assetId}`)
        .then((res) => {
          switch (res.status) {
            case 200: {
              return res.body;
            }
            default: {
              throw new UnknownApiError('server returned with unhandled status code', res);
            }
          }
        });
    }.bind(api),

    /**
     * This method removes an asset from the list of favourite assets of the logged in user
     *
     * Auth token is required.
     *
     * @method
     * @this API
     *
     * @param {Object} payload
     * @param {string=} payload.assetId
     *
     * @return {Promise} promise
     * @fulfill {Object}
     * @reject {AuthTokenNotSetError}
     * @reject {ArgumentError} Asset ID is required
     * @reject {UnknownApiError} Server returned with unexpected status code
     * */
    removeFavourite: function removeFavourite(payload = {}) {
      if (!this.authData) {
        return Promise.reject(new AuthTokenNotSetError());
      }

      if (typeof payload.assetId !== 'string') {
        return Promise.reject(new ArgumentError('payload.assetId is required and must be a string'));
      }

      const {
        assetId,
      } = payload;

      return this.request('delete', `/my-list/v1/favourites/${assetId}`)
        .then((res) => {
          switch (res.status) {
            case 200: {
              return res.body;
            }
            default: {
              throw new UnknownApiError('server returned with unhandled status code', res);
            }
          }
        });
    }.bind(api),
  };

  return myList;
}
